import {
  createColumnHelper, useReactTable, getCoreRowModel, getSortedRowModel
} from "@tanstack/react-table";
import { type Business } from "lens-platform-sdk";
import { action } from "@k8slens/lds-icons";

import Table from "src/components/Table/Table";

import styles from "src/components/Business/ParentTable.module.css";
import { ContextMenu, ContextMenuItem } from "src/components/ContextMenu/ContextMenu";
import { ContextMenuIcon } from "src/components/Icon/Navigation";
import { useParams } from "react-router-dom";
import { useBusinessParent } from "src/hooks/useBusinessParent";
import { useMemo } from "react";
import Notification from "src/components/Notification/Notification";
import { useTrackedTableSort } from "./useTrackedTableSort";

const { DeleteIcon } = action;

const parentTableColumnHelper = createColumnHelper<Business>();

const caption = "Parent account";

export const ParentTable = () => {
  const { businessId: childBusinessId } = useParams<{ businessId: string }>();

  const {
    businessParent,
    loadingBusinessParent,
    removeBusinessParent,
    errorRemovingBusinessParent
  } = useBusinessParent(childBusinessId);

  const parentTableColumns = useMemo(() => [
    parentTableColumnHelper.accessor("name", {
      header: () => <span>Name</span>,
      cell: ({ getValue }) => (
        <div className={styles.name}>
          {getValue()}
        </div>
      )
    }),
    parentTableColumnHelper.accessor("department", {
      header: () => <span>Department</span>,
      cell: ({ getValue }) => (
        <div className={styles.department}>
          {getValue() ?? "N/A"}
        </div>
      )
    }),
    parentTableColumnHelper.accessor("country", {
      header: () => <span>Country</span>,
      cell: ({ getValue }) => (
        <div className={styles.country}>
          {getValue() ?? "N/A"}
        </div>
      )
    }),
    parentTableColumnHelper.accessor("id", {
      header: () => <span />,
      enableSorting: false,
      cell: () => (
        <div className={styles.menuContainer}>
          <ContextMenu toggleIcon={ContextMenuIcon} toggleButtonProps={{ title: "Toggle context menu for parent" }}>
            <ContextMenuItem label="Remove" icon={DeleteIcon} onClick={() => removeBusinessParent(childBusinessId)} />
          </ContextMenu>
        </div>
      )
    })
  ], [childBusinessId, removeBusinessParent]);

  const { sorting, onSortingChange } = useTrackedTableSort({ table: caption });

  const parentTable = useReactTable({
    data: businessParent ? [businessParent] : [],
    columns: parentTableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting
    },
    onSortingChange
  });
  const parentTableRowModel = parentTable.getRowModel();
  const parentTableRows = useMemo(() => parentTableRowModel.rows, [parentTableRowModel]);

  return (
    <>
      <Table<Business>
        caption={caption}
        loading={loadingBusinessParent}
        className={styles.table}
        rows={parentTableRows}
        columns={parentTable.getAllColumns()}
        headerGroups={parentTable.getHeaderGroups()}
        headerGroupProps={{
          cellClassName: styles.tableHeader
        }}
        rowProps={{
          className: styles.tableRow,
          cellClassName: styles.tableCell
        }}
        noDataText="No parent account"
      />
      {
        errorRemovingBusinessParent ? (
          <div className="mt-1 mb-1">
            <Notification
              level="error"
              message={errorRemovingBusinessParent.message}
              type="closable"
            />
          </div>
        ) : null
      }
    </>
  );
};
